import { EmojiPickerV4 } from "@circle-react-uikit/EmojiPickerV4";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import noop from "lodash/noop";
import type { PopperProps } from "react-popper";

import { CUSTOM_ICON_LIGHT_DATA_SET, CUSTOM_SHAPE_DATA_SET } from "./data";
import { EmojiPickerProvider } from "./EmojiPickerContext";
import type { EmojiPickerProviderValues } from "./EmojiPickerContext";
import { EmojiPopOver } from "./EmojiPopover";

interface EmojiSkin {
  src: string;
}

export interface Emoji {
  id: string;
  name: string;
  short_names: string[];
  skins: EmojiSkin[];
}

export interface CustomEmoji {
  emojis: Emoji[];
  id: string;
  name: string;
}

export interface EmojiData {
  emoji?: string;
  emoji_type: string;
  identifier?: string;
}

export type EmojiPickerAddOns = "icons" | "shapes" | "custom" | "emoji";

export interface EmojiPickerV3Props extends EmojiPickerProviderValues {
  pickerAddOns?: EmojiPickerAddOns[];
  placement?: PopperProps<any>["placement"];
  popperOptions?: PopperProps<any>["modifiers"];
}

export const EmojiPickerV3 = ({
  as = "button",
  buttonAriaLabel,
  target,
  popperOptions,
  value,
  name,
  onRemoveEmoji,
  targetWrapperClassName = "",
  popoverPanelClassName = "",
  popoverRootClassName = "",
  placement = "bottom-start",
  pickerAddOns = [],
  handleCustomIconUpload = noop,
  onEmojiSelect = noop,
  customIcons = CUSTOM_ICON_LIGHT_DATA_SET,
  customShapeIcons = CUSTOM_SHAPE_DATA_SET,
  shouldShowEmojiPreview = false,
  shouldShowSkinTonePicker = true,
  shouldEmojiPickerRenderAsPortal = true,
  shouldCloseOnSelect = true,
  portalTo,
}: EmojiPickerV3Props) => {
  const { isCircleV4 } = useCircleV4();

  if (isCircleV4) {
    const v4AddOns = pickerAddOns.filter(
      (a): a is "shapes" | "icons" | "custom" =>
        a === "shapes" || a === "icons" || a === "custom",
    );
    return (
      <EmojiPickerV4
        as={as}
        buttonAriaLabel={buttonAriaLabel}
        target={target}
        targetWrapperClassName={targetWrapperClassName}
        value={value}
        name={name}
        onRemoveEmoji={onRemoveEmoji}
        pickerAddOns={v4AddOns}
        handleCustomIconUpload={handleCustomIconUpload}
        onEmojiSelect={onEmojiSelect}
        customShapeIcons={customShapeIcons}
        customIcons={customIcons}
        shouldShowSkinTonePicker={shouldShowSkinTonePicker}
        shouldCloseOnSelect={shouldCloseOnSelect}
      />
    );
  }

  return (
    <EmojiPickerProvider
      pickerAddOns={pickerAddOns}
      placement={placement}
      popperOptions={popperOptions}
      value={{
        as,
        buttonAriaLabel,
        targetWrapperClassName,
        popoverPanelClassName,
        popoverRootClassName,
        shouldShowEmojiPreview,
        target,
        shouldEmojiPickerRenderAsPortal,
        value,
        name,
        onRemoveEmoji,
        handleCustomIconUpload,
        onEmojiSelect,
        customIcons,
        customShapeIcons,
        shouldShowSkinTonePicker,
        shouldCloseOnSelect,
        portalTo,
      }}
    >
      <EmojiPopOver />
    </EmojiPickerProvider>
  );
};
